import { Outlet, useOutletContext } from 'react-router-dom'

import { ApolloError } from '@apollo/client'
import { lastDayOfMonth, startOfMonth, subMonths } from 'date-fns'
import {
  InsightsFiltersOptionsDocument,
  InsightsFiltersOptionsQuery,
} from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateToString } from 'modules/core/lib/dates'

import { useInsightsFilters } from 'modules/insights/hooks/useInsightsFilters'
import CollectionsBootstrap from 'modules/insights/lib/CollectionsBootstrap'
import canSeeInsights from 'modules/insights/lib/canSeeInsights'

export type InsightsContext = ReturnType<typeof useInsightsFilters> & {
  filterOptionsData?: InsightsFiltersOptionsQuery
  filterOptionsError?: ApolloError
}

export const useInsightsContext = () => {
  return useOutletContext<InsightsContext>()
}

const Container = () => {
  const useInsightsFiltersResult = useInsightsFilters()

  const { data: filterOptionsData, error: filterOptionsError } = useQuery(
    InsightsFiltersOptionsDocument,
    {
      variables: {
        // Taking only the last year values
        fromDate: dateToString(startOfMonth(subMonths(new Date(), 11)), 'backend'),
        toDate: dateToString(lastDayOfMonth(new Date()), 'backend'),
      },
    }
  )

  return (
    <AuthorizeUser authorizeFn={() => canSeeInsights()}>
      <Outlet
        context={
          {
            ...useInsightsFiltersResult,
            filterOptionsData,
            filterOptionsError,
          } satisfies InsightsContext
        }
      />
    </AuthorizeUser>
  )
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
